import Model, { belongsTo, hasMany, attr } from '@ember-data/model'
import { Type } from '@warp-drive/core-types/symbols'
import type SchoolClass from 'district-ui-client/models/school-class'
import type School from 'district-ui-client/models/school'
import type Teacher from 'district-ui-client/models/teacher'
import { uniqBy } from 'lodash'
import type { SubscriptionType } from 'district-ui-client/domain/subscription-type'
import type NumberTransform from 'district-ui-client/transforms/number'
import type StringTransform from 'district-ui-client/transforms/string'

function uniqTeachersFromSchoolClasses(schoolClasses: SchoolClass[]): Teacher[] {
  return uniqBy(
    schoolClasses.reduce<Teacher[]>(
      (acc, schoolClass) => (schoolClass.teachers ? [...acc, ...schoolClass.teachers] : acc),
      [],
    ),
    (teacher) => teacher.id,
  )
}

export default class Student extends Model {
  @attr<StringTransform>('string') declare firstName: string

  @attr<StringTransform>('string') declare lastName: string

  @attr<NumberTransform>('number') declare gradePosition: number

  @attr<StringTransform>('string') declare login: string

  @attr<StringTransform>('string') declare plainPassword: string

  @attr<StringTransform>('string') declare externalId: string

  @belongsTo<School>('school', { async: false, inverse: 'students' }) declare school?: School

  @hasMany<SchoolClass>('school-class', { async: false, inverse: 'students' }) declare schoolClasses: SchoolClass[]

  get schoolName() {
    return this.school?.name ?? ''
  }

  get fullName() {
    return `${this.firstName} ${this.lastName}`
  }

  schoolClassesFor(subscriptionType: SubscriptionType) {
    return this.schoolClasses?.filter((item) => item.subscriptionType === subscriptionType) ?? []
  }

  teachersFor(subscriptionType: SubscriptionType) {
    return uniqTeachersFromSchoolClasses(this.schoolClassesFor(subscriptionType))
  }

  [Type] = 'student' as const
}
